import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useWebphoneStore } from '@/stores/webphoneStore';

const KAVKOM_URL = 'https://webphone.kavkom.com/dialpad';
const WIDTH = 376;
const HEIGHT = 666;

export const WebphonePopover: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { isOpen, close } = useWebphoneStore();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isRTL = i18n.language === 'he' || i18n.language === 'ar';

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current && !containerRef.current.contains(target)) {
        // Ignore clicks on the toggle button itself (it handles its own toggle)
        const toggle = (target as HTMLElement)?.closest?.('[aria-label="Webphone"]');
        if (toggle) return;
        close();
      }
    };
    // Defer one tick so the opening click doesn't immediately close it
    const id = window.setTimeout(() => {
      window.addEventListener('mousedown', onClick);
    }, 0);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('mousedown', onClick);
    };
  }, [isOpen, close]);

  // Position: anchored to the sidebar (sidebar width is 4rem / 5rem / 5vw).
  // Use lg breakpoint approximation; on small screens fall back to a fixed offset.
  const sideOffset = '5.25rem';
  const positionStyle: React.CSSProperties = {
    position: 'fixed',
    top: '4rem',
    width: WIDTH,
    height: HEIGHT,
    zIndex: 60,
    [isRTL ? 'right' : 'left']: sideOffset,
    // Hide when closed but keep mounted so the Kavkom session/cookies persist.
    visibility: isOpen ? 'visible' : 'hidden',
    pointerEvents: isOpen ? 'auto' : 'none',
    opacity: isOpen ? 1 : 0,
    transition: 'opacity 150ms ease-out',
  };

  return (
    <div
      ref={containerRef}
      style={positionStyle}
      className="rounded-xl shadow-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 overflow-hidden flex flex-col"
      role="dialog"
      aria-label={t('webphone.title', 'Webphone')}
      aria-hidden={!isOpen}
    >
      <div className="flex items-center justify-between h-9 px-3 border-b border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900">
        <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
          {t('webphone.title', 'Webphone')}
        </span>
        <button
          type="button"
          onClick={close}
          className="p-1 rounded hover:bg-neutral-200 dark:hover:bg-neutral-700 text-neutral-600 dark:text-neutral-400"
          aria-label={t('common.close', 'Close')}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <iframe
        src={KAVKOM_URL}
        title="Kavkom Webphone"
        className="flex-1 w-full border-0 bg-white"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
        allow="microphone; camera; autoplay; clipboard-read; clipboard-write"
      />
    </div>
  );
};
